import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Star, TrendingUp } from "lucide-react";
import { CONTACT, IMG } from "../data/site";
import { Marker, ease } from "./shared";
import { useI18n } from "../i18n/LanguageContext";

export const Hero = () => {
  const { t, c } = useI18n();
  const h = t.hero;
  const featured = c.PROJECTS[4];

  return (
    <section id="top" className="relative pt-28 sm:pt-32" data-testid="hero-section">
      <div className="container-x">
        <div className="relative overflow-hidden rounded-[2rem] bg-navy px-6 py-12 sm:rounded-[2.75rem] sm:px-10 sm:py-14 lg:px-16 lg:py-20">
          <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-gold/20 blur-[120px]" />
          <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-navy-600/40 blur-[120px]" />

          <div className="relative grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <motion.span initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-gold-300">
                {h.badge}
              </motion.span>

              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 0.1 }}
                className="mt-6 font-display text-[13vw] font-semibold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl">
                {h.title1}{" "}
                <Marker><span className="text-gold-300">{h.accent}</span></Marker>
              </motion.h1>

              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.25 }}
                className="mt-7 max-w-md text-base leading-relaxed text-white/70 sm:text-lg">
                {h.sub} <span className="font-medium text-white">{h.subBold}</span>
              </motion.p>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease, delay: 0.38 }}
                className="mt-9 flex flex-wrap items-center gap-4">
                <Link to="/projects" data-testid="hero-browse-btn" className="btn-primary group">
                  {h.browse}
                  <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <a href={CONTACT.calendly} target="_blank" rel="noreferrer" data-testid="hero-call-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-white hover:text-navy">
                  {h.call}
                </a>
              </motion.div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }}
                className="mt-11 flex flex-wrap items-center gap-x-8 gap-y-4" data-testid="hero-trust">
                <div className="flex items-center gap-2.5">
                  <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} size={15} className="fill-gold-300 text-gold-300" />)}</div>
                  <span className="text-sm text-white/80"><span className="font-semibold text-white">4.7</span> {h.trustpilot}</span>
                </div>
                <div className="h-8 w-px bg-white/15" />
                <span className="text-sm text-white/80"><span className="font-semibold text-white">250+</span> {h.deals}</span>
              </motion.div>
            </div>

            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease, delay: 0.2 }} className="relative">
              <div className="relative overflow-hidden rounded-[1.5rem] sm:rounded-[2rem]">
                <img src={IMG.skyline} alt="Dubai skyline at golden hour" className="h-[340px] w-full object-cover sm:h-[440px] lg:h-[520px]" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent" />
              </div>

              <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-2 bottom-6 w-52 rounded-2xl bg-white p-4 shadow-card sm:-left-6">
                <span className="text-xs font-medium uppercase tracking-wide text-muted">{h.nowLaunching}</span>
                <p className="mt-1 font-display text-lg font-semibold text-navy">{featured.name}</p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gold">{featured.price} {featured.currency}</span>
                  <span className="rounded-full bg-gold/10 px-2 py-0.5 text-xs font-medium text-gold">{featured.developer}</span>
                </div>
              </motion.div>

              <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -right-1 top-6 flex items-center gap-2 rounded-2xl bg-gold px-4 py-3 shadow-gold sm:-right-4">
                <TrendingUp size={18} className="text-white" />
                <div className="leading-tight text-white">
                  <p className="font-display text-lg font-semibold">+30–40%</p>
                  <p className="text-[11px] opacity-90">{h.appreciation}</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
